(() => {
  const root = document.documentElement;
  root.classList.remove('no-js');
  root.classList.add('js');
  const body = document.body;

  const initMobileMenu = (scope = document) => {
    const toggle = scope.querySelector?.('[data-mobile-toggle]') || document.querySelector('[data-mobile-toggle]');
    const menu = document.querySelector('[data-mobile-menu]');
    if (!toggle || !menu || toggle.dataset.leaReady === 'true') return;
    toggle.dataset.leaReady = 'true';
    const label = toggle.querySelector('[data-mobile-toggle-label]');
    const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const header = document.querySelector('.site-header');
    const main = document.querySelector('main');
    const footer = document.querySelector('footer');
    let previousFocus = null;

    const updateMenuOffset = () => {
      const headerBottom = header?.getBoundingClientRect().bottom || 0;
      menu.style.setProperty('--lea-mobile-menu-top', `${Math.max(0, Math.round(headerBottom))}px`);
    };

    const setMenuState = (open, restoreFocus = true) => {
      if (open) updateMenuOffset();
      menu.classList.toggle('is-open', open);
      menu.hidden = !open;
      body.classList.toggle('menu-open', open);
      main?.toggleAttribute('inert', open);
      footer?.toggleAttribute('inert', open);
      toggle.setAttribute('aria-expanded', String(open));
      if (label) label.textContent = open ? 'Close menu' : 'Open menu';
      if (open) {
        previousFocus = document.activeElement;
        menu.querySelector(focusableSelector)?.focus();
      } else if (restoreFocus && previousFocus instanceof HTMLElement) {
        previousFocus.focus();
      }
    };

    toggle.addEventListener('click', () => {
      setMenuState(toggle.getAttribute('aria-expanded') !== 'true');
    });

    menu.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMenuState(false);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...menu.querySelectorAll(focusableSelector)];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => {
      const isSamePageTarget = link.hash
        && link.origin === window.location.origin
        && link.pathname === window.location.pathname;
      setMenuState(false, false);
      if (isSamePageTarget) {
        window.requestAnimationFrame(() => {
          const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
          if (!target) return;
          if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        });
      }
    }));

    window.addEventListener('resize', () => {
      if (window.innerWidth > 900 && toggle.getAttribute('aria-expanded') === 'true') {
        setMenuState(false, false);
      } else if (toggle.getAttribute('aria-expanded') === 'true') {
        updateMenuOffset();
      }
    });
  };

  const initReveal = (scope = document) => {
    const reveal = scope.querySelectorAll?.('[data-reveal]') || [];
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reveal.length) return;
    if ('IntersectionObserver' in window && !reduceMotion) {
      const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      }), { threshold: 0.12 });
      reveal.forEach((element) => observer.observe(element));
      root.classList.add('reveal-ready');
    } else {
      reveal.forEach((element) => element.classList.add('is-visible'));
    }
  };

  const initPricing = (scope = document) => {
    scope.querySelectorAll?.('[data-pricing]').forEach((pricing) => {
      if (pricing.dataset.leaReady === 'true') return;
      pricing.dataset.leaReady = 'true';
      const buttons = pricing.querySelectorAll('[data-price-period]');
      const value = pricing.querySelector('[data-price-value]');
      const suffix = pricing.querySelector('[data-price-suffix]');
      buttons.forEach((button) => button.addEventListener('click', () => {
        buttons.forEach((candidate) => {
          candidate.classList.toggle('is-active', candidate === button);
          candidate.setAttribute('aria-pressed', String(candidate === button));
        });
        const period = button.dataset.pricePeriod;
        if (value) value.textContent = value.dataset[period] || '—';
        if (suffix) suffix.textContent = suffix.dataset[period] || '';
      }));
    });
  };

  const initDropdowns = (scope = document) => {
    scope.querySelectorAll?.('details.nav-dropdown').forEach((detail) => {
      if (detail.dataset.leaReady === 'true') return;
      detail.dataset.leaReady = 'true';
      document.addEventListener('click', (event) => {
        if (!detail.contains(event.target)) detail.removeAttribute('open');
      });
      detail.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          detail.removeAttribute('open');
          detail.querySelector('summary')?.focus();
        }
      });
    });
  };

  const initFormStatus = (scope = document) => {
    const status = scope.querySelector?.('[data-form-status]');
    if (status && status.dataset.leaFocused !== 'true') {
      status.dataset.leaFocused = 'true';
      status.focus();
    }
  };

  const initDemoVideos = (scope = document) => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    scope.querySelectorAll?.('.lea-demo-video-shell').forEach((shell) => {
      if (shell.dataset.leaVideoReady === 'true') return;
      shell.dataset.leaVideoReady = 'true';

      const video = shell.querySelector('.lea-demo-video');
      const toggle = shell.querySelector('[data-demo-video-toggle]');
      if (!video || !toggle) return;

      video.muted = true;
      video.playsInline = true;
      let visible = false;
      const autoplayEnabled = shell.dataset.demoAutoplay === 'true' && !reduceMotion;
      let userPaused = !autoplayEnabled;

      const updateState = () => {
        const playing = !video.paused && !video.ended;
        shell.classList.toggle('is-playing', playing);
        shell.classList.remove('is-buffering');
        toggle.setAttribute('aria-label', playing ? 'Pause product demo' : 'Play product demo');
        toggle.setAttribute('aria-pressed', String(playing));
      };

      const playVideo = () => {
        if (userPaused || document.hidden || !visible) return;
        shell.classList.add('is-buffering');
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            shell.classList.remove('is-buffering');
            updateState();
          });
        }
      };

      const pauseVideo = () => {
        video.pause();
        updateState();
      };

      video.addEventListener('playing', updateState);
      video.addEventListener('pause', updateState);
      video.addEventListener('waiting', () => shell.classList.add('is-buffering'));
      video.addEventListener('canplay', () => shell.classList.remove('is-buffering'));

      toggle.addEventListener('click', () => {
        if (video.paused) {
          userPaused = false;
          visible = true;
          playVideo();
        } else {
          userPaused = true;
          pauseVideo();
        }
      });

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            visible = entry.isIntersecting && entry.intersectionRatio >= 0.4;
            if (visible) playVideo();
            else pauseVideo();
          });
        }, { threshold: [0, 0.4, 0.75] });
        observer.observe(shell);
      } else {
        visible = true;
        if (!reduceMotion) playVideo();
      }

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) pauseVideo();
        else playVideo();
      });

      updateState();
    });
  };

  const initProductShowcases = (scope = document) => {
    scope.querySelectorAll?.('[data-product-showcase]').forEach((showcase) => {
      if (showcase.dataset.leaReady === 'true') return;
      showcase.dataset.leaReady = 'true';

      const scroller = showcase.querySelector('[data-product-scroller]');
      const cards = [...showcase.querySelectorAll('[data-product-card]')];
      const previous = showcase.querySelector('[data-product-prev]');
      const next = showcase.querySelector('[data-product-next]');
      const status = showcase.querySelector('[data-product-status]');
      if (!scroller || !cards.length || !previous || !next) return;

      let activeIndex = 0;
      let frame = 0;

      const cardLeft = (card) => card.offsetLeft - scroller.offsetLeft;
      const update = () => {
        activeIndex = cards.reduce((closest, card, index) => {
          const distance = Math.abs(cardLeft(card) - scroller.scrollLeft);
          const closestDistance = Math.abs(cardLeft(cards[closest]) - scroller.scrollLeft);
          return distance < closestDistance ? index : closest;
        }, 0);
        previous.disabled = activeIndex === 0;
        next.disabled = activeIndex === cards.length - 1;
        if (status) status.textContent = `${activeIndex + 1} / ${cards.length}`;
      };

      const goTo = (index) => {
        const targetIndex = Math.max(0, Math.min(cards.length - 1, index));
        activeIndex = targetIndex;
        previous.disabled = activeIndex === 0;
        next.disabled = activeIndex === cards.length - 1;
        if (status) status.textContent = `${activeIndex + 1} / ${cards.length}`;
        scroller.scrollTo({
          left: cardLeft(cards[targetIndex]),
          behavior: 'auto',
        });
      };

      previous.addEventListener('click', () => goTo(activeIndex - 1));
      next.addEventListener('click', () => goTo(activeIndex + 1));
      scroller.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        goTo(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
      });
      scroller.addEventListener('scroll', () => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(update);
      }, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  };

  const initAll = (scope = document) => {
    [
      initMobileMenu,
      initReveal,
      initPricing,
      initDropdowns,
      initDemoVideos,
      initProductShowcases,
      initFormStatus,
    ].forEach((initializer) => {
      try {
        initializer(scope);
      } catch (error) {
        console.warn(`LEA theme initialization skipped: ${initializer.name}`, error);
      }
    });
  };

  initAll(document);
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
